import { apiFetch, getAuthHeaders } from '../../services/http';
import type { TaskRecord, DealRecord } from '../../types/crm';

export type TaskListResponse = {
  items: TaskRecord[];
  deals: DealRecord[];
  total: number;
  summary: {
    total: number;
    pending: number;
    completed: number;
    cancelled: number;
  };
};

export type CompleteTaskPayload = {
  resolution_notes?: string;
  deal_stage_id?: string;
  resolve_conversation?: boolean;
  next_task_title?: string;
  next_task_description?: string;
  next_task_due_date?: string;
  next_task_priority?: 'low' | 'medium' | 'high' | 'urgent';
  next_task_type?: string;
};

export type CreateTaskPayload = {
  title: string;
  description?: string;
  pipeline_item_id?: string;
  deal_id?: string;
  task_type?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  due_date?: string;
  status?: string;
};

export type UpdateTaskPayload = Partial<CreateTaskPayload>;

export const tasksApi = {
  list: (token: string, status?: string) => {
    const url = status && status !== 'all' ? `/v1/app/tasks?status=${status}` : '/v1/app/tasks';
    return apiFetch<TaskListResponse>(url, {
      headers: getAuthHeaders(token),
    });
  },
  create: (token: string, payload: CreateTaskPayload) =>
    apiFetch<TaskRecord>('/v1/app/tasks', {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  update: (token: string, taskId: string, payload: UpdateTaskPayload) =>
    apiFetch<TaskRecord>(`/v1/app/tasks/${taskId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  complete: (token: string, taskId: string, payload?: CompleteTaskPayload) =>
    apiFetch<{ task: TaskRecord; nextTask?: TaskRecord; success: boolean }>(`/v1/app/tasks/${taskId}/complete`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: payload ? JSON.stringify(payload) : undefined,
    }),
  reopen: (token: string, taskId: string) =>
    apiFetch<TaskRecord>(`/v1/app/tasks/${taskId}/reopen`, {
      method: 'POST',
      headers: getAuthHeaders(token),
    }),
  cancel: (token: string, taskId: string) =>
    apiFetch<TaskRecord>(`/v1/app/tasks/${taskId}/cancel`, {
      method: 'POST',
      headers: getAuthHeaders(token),
    }),
  delete: (token: string, taskId: string) =>
    apiFetch<{ success: boolean; deleted: string }>(`/v1/app/tasks/${taskId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(token),
    }),
  summarizeWithAi: (token: string, payload: { task_id?: string; task_title: string; task_description?: string; deal_name?: string }) =>
    apiFetch<{ summary: string; recommended_follow_up?: string; follow_up_delay_days?: number }>('/v1/app/tasks/ai-summarize', {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
};
